import React, { type FC, memo } from 'react';
import type { FormApi } from 'final-form';
import type { IProps as IDcFormShortProps } from '@alfa-bank/site-dc-form-short/src/models';
import { NamedContainer } from '@alfa-bank/site-named-container';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
    firstStepRequest,
    selectBasketData,
    selectFirstStepData,
    setServices,
} from '@alfa-bank/site-dc-form-api/store';
import type { IFirstStepDataFromView } from '@alfa-bank/site-dc-form-api/src/store/features/models';

import { preparePhoneValue } from '../../utils/helpers';
import { firstStepAnalytics as analytics } from '../../analytics';

import type stylesDesktop from './styles/desktop.css';
import type { TFirstStep, IFirstStepFormValues } from './models';
import { formatBirthdayValue, prepareCashbackData, getAdditionalProducts } from './helpers';
import type { IAdditionalProductFieldProps } from './components/additional-product/models';

export const FirstStep: FC<TFirstStep> = memo(
    ({
        AdditionalProductField,
        Form,
        Text,
        abTest,
        additionalProducts,
        birthday,
        bonusCard,
        card,
        cashback,
        cashbackPackages,
        children,
        client,
        clientPlate,
        defaultAdditionalServices = [],
        email: emailProps,
        embossing,
        fio,
        flexibleAlfaCard,
        formAction,
        formHash,
        heading,
        isMobile,
        notResident,
        phone: phoneProps,
        product,
        productDesign,
        progress,
        progressType,
        refId,
        resident,
        scenarioId,
        sopdList,
        styles,
        subHeading,
        withDraftFirstStep,
    }) => {
        const dispatch = useDispatch();
        const { groupId, offerId, products, selectedCategories, services, subscription } =
            useSelector(selectBasketData);
        const { cardId: designCardId } = useSelector(selectFirstStepData);
        const { cardId, contractId, packageId } = card;
        const { additionalField, additionalFieldTop } = useNamedContainers(children);
        const hasAdditionalProductsCarousel =
            additionalProducts?.products && additionalProducts.products.length > 1;

        const onSubmit = (formRef: HTMLFormElement, form: FormApi<IFirstStepFormValues>) => {
            const {
                additionalProductsArray,
                bonusNumber,
                bonusNumberTag,
                cashbackCategories,
                cashbackPackage,
                email,
                embossingName,
                firstName,
                isClient,
                lastName,
                middleName,
                packageCashbackArray,
                passportBirthDate,
                phone,
                productDesignCardId,
                selectedCashbackType,
                sex,
            } = form.getState().values;

            const formattedBirthday = formatBirthdayValue(passportBirthDate);
            const formattedPhone = preparePhoneValue(phone.toString());
            const formattedBonusNumber = String(bonusNumber);

            const firstStepData: IFirstStepDataFromView = {
                cardId: productDesignCardId || designCardId || cardId,
                packageId,
                contractId,
                email,
                embossingName,
                firstName,
                lastName,
                middleName,
                passportBirthDate: formattedBirthday,
                mobilePhone: formattedPhone,
                sex,
                sopdConfirmed: true,
                additionalProducts: getAdditionalProducts({
                    basketProducts: products,
                    defaultProducts: additionalProducts?.defaultProducts,
                    additionalProducts: additionalProductsArray,
                    showCarousel: hasAdditionalProductsCarousel,
                }),
                additionalServices: [...defaultAdditionalServices, ...services],
                packageCashback: prepareCashbackData({
                    cashbackCategories,
                    packageCashbackArray,
                    cashbackPackage,
                    selectedCashbackType,
                    basket: { subscription, selectedCategories, groupId, offerId },
                }),
                abTest,
                isClient: isClient === 'client',
                bonusNumber: bonusNumberTag === 'visible' ? formattedBonusNumber : undefined,
                scenarioId,
                sopdList,
                refId,
            };

            dispatch(firstStepRequest(firstStepData));

            if (defaultAdditionalServices.length) {
                dispatch(setServices(defaultAdditionalServices));
            }
        };

        return (
            <>
                {heading && (
                    <Text
                        styler={{
                            marginTop: 0,
                            marginBottom: isMobile ? 12 : 16,
                            paddingBottom: isMobile ? 0 : 2,
                        }}
                        view={isMobile ? 'small' : 'medium'}
                    >
                        {heading}
                    </Text>
                )}
                <Form
                    additionalProducts={
                        hasAdditionalProductsCarousel ? additionalProducts : undefined
                    }
                    analytics={analytics}
                    birthday={birthday}
                    bonusCard={bonusCard}
                    cards={{
                        label: '',
                        cardId,
                        packetId: packageId,
                        prefillContractId: contractId,
                    }}
                    carousel={{
                        containerClassName:
                            flexibleAlfaCard && !isMobile
                                ? cn(
                                      (styles as typeof stylesDesktop)
                                          .flexibleDesignCarouselContainer,
                                      styles?.external,
                                      styles?.override
                                  )
                                : cn(styles?.carouselContainer, styles?.external, styles?.override),

                        contentClassName: flexibleAlfaCard
                            ? cn(
                                  (styles as typeof stylesDesktop).flexibleDesignCarouselContent,
                                  styles?.external,
                                  styles?.override
                              )
                            : cn(styles?.carouselContent, styles?.external, styles?.override),
                        carouselTitle: flexibleAlfaCard
                            ? cn((styles as typeof stylesDesktop).carouselTitle, styles?.external)
                            : undefined,
                    }}
                    cashback={cashback}
                    cashbackPackages={cashbackPackages}
                    client={client}
                    clientPlate={clientPlate}
                    dataTestId="dc-form-first-step"
                    draft={{}}
                    email={emailProps}
                    embossing={embossing}
                    fio={fio}
                    flexibleAlfaCard={flexibleAlfaCard}
                    formAction={formAction}
                    formHash={formHash}
                    isNewDesign={!flexibleAlfaCard}
                    mobileIdDisabled
                    notResident={notResident}
                    oneFieldFio={fio.oneFieldFio}
                    onSubmit={onSubmit as unknown as IDcFormShortProps['onSubmit']}
                    paySystem={[{ label: 'Мир', value: 'mir' }]}
                    phone={phoneProps}
                    product={product}
                    productDesign={productDesign}
                    progress={progress}
                    progressType={progressType}
                    rejectPhone940
                    resident={resident}
                    securityText="Мы гарантируем безопасность и сохранность ваших данных"
                    showFormOuterWrapper={false}
                    subHeading={subHeading}
                    typeSubmitButton={'securityWarrantyBlock'}
                    withDraftFirstStep={withDraftFirstStep}
                >
                    <NamedContainer name="additionalField">
                        {additionalProducts && additionalProducts.products.length === 1 && (
                            <AdditionalProductField
                                isFlexibleAlfaCard={flexibleAlfaCard}
                                {...(additionalProducts as IAdditionalProductFieldProps)}
                            />
                        )}
                        {additionalField}
                    </NamedContainer>
                    <NamedContainer name="additionalFieldTop">{additionalFieldTop}</NamedContainer>
                    {children}
                </Form>
            </>
        );
    }
);
